import { useCallback, useSyncExternalStore } from 'react';

import type { PreviewOverlay } from '@/lib/crm/preview';
import { useActivePreviewHandle } from '@/lib/crm/preview';

const NOOP_OVERLAY_SUBSCRIBE = (_cb: () => void) => () => {};

/**
 * Subscribes to the CRM preview overlay for a given objectId, returning
 * the current overlay value (or undefined if none exists).
 *
 * The handle comes from `useActivePreviewHandle`: the context when this hook
 * renders inside a provider subtree, otherwise the ACTIVE registered
 * provider. The fallback is what lets the record slideout — root-portaled
 * (`SlideoutRenderer`) or hosted as a `SplitView` sibling
 * (`InlineCrmObjectPanel`) — resolve projections from a proposal detail
 * panel mounted elsewhere in the tree. A provider mounting/unmounting
 * re-renders this hook (registry subscription inside the handle hook), which
 * re-subscribes to the new handle's overlay.
 */
export function useOverlaySubscription(
  objectId: string
): PreviewOverlay | undefined {
  const preview = useActivePreviewHandle();

  const overlaySubscribe = useCallback(
    (cb: () => void) =>
      preview
        ? preview.subscribeToOverlay(objectId, cb)
        : NOOP_OVERLAY_SUBSCRIBE(cb),
    [preview, objectId]
  );
  const overlayGetSnapshot = useCallback(
    () => (preview ? preview.getOverlay(objectId) : undefined),
    [preview, objectId]
  );
  return useSyncExternalStore(overlaySubscribe, overlayGetSnapshot);
}
